import type { ProblemJSDependencies } from '../problemJSDependencies';
import { BasicObject, getBasicObject } from './basicObject';
import {
  getStringsAndValuesParagraph,
  StringsAndValuesParagraphWrapperDTO,
} from './stringsAndValuesParagraph';

const BULLET_SIZE = 6;
const SPACE_AFTER_BULLET = 10;
const INDENT_SAVP = BULLET_SIZE + SPACE_AFTER_BULLET;
const GAP_BETWEEN_ITEMS = 8;

interface ListOfSAVPsDTO {
  stringsAndValuesParagraphs: StringsAndValuesParagraphWrapperDTO[];
}

export interface BulletedListDTO {
  bulletedList: ListOfSAVPsDTO;
}

export function getBulletedList(
  problemJS: ProblemJSDependencies,
  bulletedListDTO: BulletedListDTO,
  maxW: number
): BasicObject {
  return new BulletedList(problemJS, bulletedListDTO, maxW);
}

class BulletedList extends BasicObject {
  gmmName = 'bulletedList';

  savps: BasicObject[] = [];

  constructor(
    problemJs: ProblemJSDependencies,
    bulletedListDTO: BulletedListDTO,
    maxW: number
  ) {
    super();
    const bulletedList = bulletedListDTO.bulletedList;

    this.savps = bulletedList.stringsAndValuesParagraphs.map(data =>
      getStringsAndValuesParagraph(problemJs, data, maxW - INDENT_SAVP, 0)
    );
    this.layout();
  }

  layout(): void {
    let y = 0;

    for (const savp of this.savps) {
      this.add(buildBullet(savp, y));

      savp.viewportX = INDENT_SAVP;
      savp.viewportY = y;
      this.add(savp);

      y += savp.viewportH + GAP_BETWEEN_ITEMS;
    }

    this.sizeMeToFitChildren();
  }

  getVertToBaseline(): number {
    let ret = 0;

    if (this.savps.length > 0) ret = this.savps[0].getVertToBaseline();

    return ret;
  }

  isBaselineAware(): boolean {
    return true;
  }
}

const buildBullet = (savp: BasicObject, y: number): BasicObject => {
  let bulletY = y;

  // Bullet alignment with baseline of first line, then adjusted per font ascent during painting
  if (savp.children.length > 0) {
    const firstLine = savp.children[0];
    const baseline = firstLine.getVertToBaseline();
    bulletY = y + baseline;
  }

  const bullet = getBasicObject(0, bulletY);
  bullet.gmmName = 'bullet';
  bullet.setAllDim(BULLET_SIZE, BULLET_SIZE);
  bullet.paintMe = (ctx: CanvasRenderingContext2D) => {
    ctx.save();
    const metrics = ctx.measureText('T');
    const ascent = metrics.actualBoundingBoxAscent;
    ctx.fillStyle = 'black';
    ctx.translate(0, -(ascent / 2));
    ctx.beginPath();
    ctx.arc(BULLET_SIZE / 2, BULLET_SIZE / 2, BULLET_SIZE / 2, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
  };

  return bullet;
};
